import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Space, Tag, Table, Popconfirm, Typography, Empty, Modal, Form, Input, Upload, Divider, Dropdown, Spin } from "antd";
import { PoweroffOutlined, CameraOutlined, ReloadOutlined, SyncOutlined, CloudUploadOutlined, InboxOutlined, ToolOutlined, ThunderboltOutlined, DownOutlined, CodeOutlined } from "@ant-design/icons";
import { enqueueDeviceCommand, listDeviceCommands, uploadAgentRelease, openTerminal, closeTerminal, terminalUrl } from "../network/api";
import { notify, serverError } from "../utils/notify";
import { useAuth } from "../context/AuthContext";
import { relativeTime } from "../constants/health";

const { Text } = Typography;

const STATUS_COLOR = { pending: "default", sent: "processing", done: "success", failed: "error" };
const TYPE_LABEL = {
  restart: "Restart",
  snapshot: "Snapshot",
  "resync-config": "Resync Config",
  "update-agent": "Update Agent",
  "run-fix": "Fix",
};

// Whitelisted remediation actions — must match the backend FIX_ACTIONS and the device
// dispatcher. `danger` ones get a confirm. `repair` is the full "make it healthy" run.
const FIX_ACTIONS = [
  { key: "repair", label: "Repair (full)", desc: "Harden + re-arm timers + test capture + apply pending" },
  { key: "rearm", label: "Re-arm timers", desc: "Enable + restart every edge360 timer" },
  { key: "restart-stack", label: "Restart stack", desc: "Restart netd / portal / door-monitor" },
  { key: "capture-test", label: "Test capture", desc: "Grab one frame and upload it" },
  { key: "apply-pending", label: "Apply pending", desc: "Re-sync config + force a command poll" },
  { key: "clear-queue", label: "Clear queue", desc: "Flush a stuck capture/upload backlog" },
  { key: "collect-logs", label: "Collect logs", desc: "Force an immediate log upload" },
  { key: "reboot", label: "Reboot", desc: "Clean reboot (last resort)", danger: true },
];

// C5-T3 — enqueue restart / snapshot commands and show their status. Commands run
// when the device next polls; a snapshot's image lands in Pictures.
export default function DeviceControls({ deviceId, online }) {
  const { isHardwareAdmin } = useAuth();
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateForm] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [fixBusy, setFixBusy] = useState(null);       // action currently being queued
  const [resultView, setResultView] = useState(null); // { type, status, text } for the output modal
  const [pwOpen, setPwOpen] = useState(false);         // terminal password prompt
  const [pwForm] = Form.useForm();
  const [termBusy, setTermBusy] = useState(false);
  const [term, setTerm] = useState(null);              // { sid, url } active terminal session

  const load = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      setCommands(await listDeviceCommands(deviceId));
    } catch (e) {
      setCommands([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (type, payload) => {
    setSending(type);
    try {
      await enqueueDeviceCommand(deviceId, type, payload);
      notify.success(`${TYPE_LABEL[type] || type} queued — runs on the device's next check-in`);
      load();
      return true;
    } catch (e) {
      notify.error(serverError(e, `Failed to queue ${type}`));
      return false;
    } finally {
      setSending(null);
    }
  };

  // Run a whitelisted remediation. Same signed command channel as the rest; the
  // device runs its /opt/edge360/fixes/<action>.sh and acks with the output, which
  // shows in the commands table (click Result to see the full text).
  const runFix = async (action) => {
    setFixBusy(action);
    try {
      await enqueueDeviceCommand(deviceId, "run-fix", { action });
      notify.success(`Fix "${action}" queued — runs on the device's next check-in`);
      load();
    } catch (e) {
      notify.error(serverError(e, `Failed to queue fix ${action}`));
    } finally {
      setFixBusy(null);
    }
  };

  // Open a one-click terminal. Gated by the separate TERMINAL_PASSWORD (asked here) on
  // top of the admin JWT. The device brings the tunnel up on its next poll (~60s), so
  // the embedded terminal may take up to a minute to connect — a reload button covers it.
  // Poll the terminal URL until the device has brought its tunnel up (returns 200).
  // The device does this on its next ~60s check-in, so opening the tab immediately would
  // show "upstream error". A 502 (not ready) either returns non-ok or trips CORS -> both
  // treated as not-ready.
  const waitForTerminal = async (url, timeoutMs) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      try {
        const ctrl = new AbortController();
        const r = await fetch(url, { mode: "cors", signal: ctrl.signal });
        const ok = r.ok;
        ctrl.abort();
        if (ok) return true;
      } catch { /* not ready yet */ }
      await new Promise((res) => setTimeout(res, 4000));
    }
    return false;
  };

  const submitTerminal = async () => {
    const { password } = await pwForm.validateFields();
    setTermBusy(true);
    let opened;
    try {
      opened = await openTerminal(deviceId, password);
    } catch (e) {
      notify.error(serverError(e, "Could not open terminal"));
      setTermBusy(false);
      return;
    }
    const full = terminalUrl(opened.url);
    setPwOpen(false);
    pwForm.resetFields();
    setTerm({ sid: opened.sessionId, url: full, ready: false });
    setTermBusy(false);
    // Wait for readiness, then reveal an Open button (a user click is required so the
    // browser doesn't block the pop-up, and the shell is guaranteed live — no 502).
    const ok = await waitForTerminal(full, 100000);
    setTerm((t) => (t && t.sid === opened.sessionId ? { ...t, ready: ok } : t));
    if (!ok) notify.warning("The device is taking longer than usual to connect — click Open Terminal when it appears, or Close and retry.");
  };
  const endTerminal = async () => {
    const sid = term?.sid;
    setTerm(null);
    if (sid) { try { await closeTerminal(deviceId, sid); } catch { /* best effort */ } }
  };

  // Update Agent — capture the release tarball URL + its sha256, then queue an
  // update-agent command. The device downloads, verifies the sha, snapshots the
  // current agent, applies, health-checks, and auto-rolls-back on failure.
  const submitUpdate = async () => {
    const values = await updateForm.validateFields();
    const ok = await send("update-agent", { url: values.url.trim(), sha256: values.sha256.trim().toLowerCase() });
    if (ok) {
      setUpdateOpen(false);
      updateForm.resetFields();
    }
  };

  // Upload a release tarball to the DMS; the server returns the public url + the
  // sha256 it computed, which we drop straight into the form (no manual copy).
  const onReleaseUpload = async (file) => {
    setUploading(true);
    try {
      const { url, sha256, filename } = await uploadAgentRelease(file);
      updateForm.setFieldsValue({ url, sha256 });
      notify.success(`Uploaded ${filename} — URL & sha256 filled in`);
    } catch (e) {
      notify.error(serverError(e, "Release upload failed"));
    } finally {
      setUploading(false);
    }
    return false; // prevent antd's default XHR upload
  };

  const columns = [
    { title: "Command", dataIndex: "type", render: (t) => <Text strong>{TYPE_LABEL[t] || t}</Text> },
    { title: "Status", dataIndex: "status", render: (s) => <Tag color={STATUS_COLOR[s] || "default"}>{s}</Tag> },
    { title: "By", dataIndex: "issuedByName", render: (v) => v || "—" },
    { title: "Issued", dataIndex: "createdAt", render: (v) => (v ? relativeTime(v) : "—") },
    {
      title: "Result", key: "result", render: (_, r) => {
        const text = r.error || r.result;
        if (!text) return r.completedAt ? "—" : <Text type="secondary">pending</Text>;
        const short = text.length > 60 ? text.slice(0, 60) + "…" : text;
        return (
          <Button type="link" size="small" style={{ padding: 0, height: "auto", textAlign: "left" }}
            onClick={() => setResultView({ type: r.type, status: r.status, text })}>
            {r.error ? <Text type="danger">{short}</Text> : short}
          </Button>
        );
      },
    },
  ];

  return (
    <Card
      title="Device Controls"
      extra={<Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>}
    >
      {isHardwareAdmin && (
        <Space wrap style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<ThunderboltOutlined />} loading={fixBusy === "repair"} onClick={() => runFix("repair")}>
            Repair
          </Button>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: FIX_ACTIONS.map((a) => ({ key: a.key, danger: a.danger, label: a.label })),
              onClick: ({ key }) => {
                const a = FIX_ACTIONS.find((x) => x.key === key);
                if (a?.danger) {
                  Modal.confirm({
                    title: `${a.label}?`, content: a.desc, okText: a.label,
                    okButtonProps: { danger: true }, onOk: () => runFix(key),
                  });
                } else runFix(key);
              },
            }}
          >
            <Button icon={<ToolOutlined />} loading={!!fixBusy}>Run Fix <DownOutlined /></Button>
          </Dropdown>
          <Button icon={<CameraOutlined />} loading={sending === "snapshot"} onClick={() => send("snapshot")}>
            Take Snapshot
          </Button>
          <Popconfirm
            title="Resync config from cloud?"
            description="The device re-pulls its configuration on its next check-in."
            okText="Resync"
            onConfirm={() => send("resync-config")}
          >
            <Button icon={<SyncOutlined />} loading={sending === "resync-config"}>Resync Config</Button>
          </Popconfirm>
          <Button icon={<CloudUploadOutlined />} loading={sending === "update-agent"} onClick={() => setUpdateOpen(true)}>
            Update Agent
          </Button>
          <Button icon={<CodeOutlined />} loading={termBusy} onClick={() => setPwOpen(true)}>
            Open Terminal
          </Button>
          <Popconfirm
            title="Reboot this device?"
            description="The device will restart on its next check-in."
            okText="Reboot"
            okButtonProps={{ danger: true }}
            onConfirm={() => send("restart")}
          >
            <Button danger icon={<PoweroffOutlined />} loading={sending === "restart"}>Restart</Button>
          </Popconfirm>
          <Text type="secondary" style={{ fontSize: 12 }}>Commands run when the device next polls.</Text>
        </Space>
      )}

      <Modal
        title="Update device agent (OTA)"
        open={updateOpen}
        onOk={submitUpdate}
        onCancel={() => setUpdateOpen(false)}
        okText="Queue update"
        confirmLoading={sending === "update-agent"}
        okButtonProps={{ icon: <CloudUploadOutlined /> }}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
          The device downloads the release tarball, verifies its sha256, snapshots the current agent,
          applies the update, health-checks, and <b>auto-rolls-back</b> if it doesn't come back healthy.
        </Text>
        <Upload.Dragger
          accept=".tgz,.tar.gz,application/gzip"
          multiple={false}
          showUploadList={false}
          disabled={uploading}
          beforeUpload={onReleaseUpload}
          style={{ marginBottom: 4 }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}><InboxOutlined /></p>
          <p className="ant-upload-text" style={{ fontSize: 13 }}>
            {uploading ? "Uploading…" : "Upload a release .tgz to publish it & auto-fill below"}
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 11 }}>
            Or paste a URL + sha256 manually if it's hosted elsewhere.
          </p>
        </Upload.Dragger>
        <Divider style={{ margin: "12px 0" }} />
        <Form form={updateForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="url"
            label="Release tarball URL"
            rules={[
              { required: true, message: "Enter the release URL" },
              { pattern: /^https:\/\/\S+$/, message: "Must be an https:// URL" },
            ]}
          >
            <Input placeholder="https://dms-api.ai360retail.com/uploads/releases/edge360-v1.1.0.tgz" />
          </Form.Item>
          <Form.Item
            name="sha256"
            label="SHA-256 digest"
            rules={[
              { required: true, message: "Enter the tarball's sha256" },
              { pattern: /^[a-fA-F0-9]{64}$/, message: "Must be a 64-character hex digest" },
            ]}
          >
            <Input placeholder="64-char hex from `sha256sum edge360-v1.1.0.tgz`" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={!!resultView}
        title={`${TYPE_LABEL[resultView?.type] || resultView?.type || "Command"} output`}
        footer={null}
        onCancel={() => setResultView(null)}
        width={760}
        destroyOnClose
      >
        {resultView?.status && <Tag color={STATUS_COLOR[resultView.status] || "default"} style={{ marginBottom: 8 }}>{resultView.status}</Tag>}
        <pre style={{ maxHeight: "60vh", overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, background: "rgba(127,127,127,0.08)", padding: 12, borderRadius: 6, margin: 0 }}>
          {resultView?.text}
        </pre>
      </Modal>

      <Modal
        title="Open device terminal"
        open={pwOpen}
        onOk={submitTerminal}
        onCancel={() => { setPwOpen(false); pwForm.resetFields(); }}
        okText="Open terminal"
        confirmLoading={termBusy}
        okButtonProps={{ icon: <CodeOutlined /> }}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
          A root shell over an encrypted, on-demand tunnel. Needs the separate <b>terminal password</b> on
          top of your admin login. The session auto-closes after its TTL; the device brings the tunnel up
          on its next check-in (up to ~60s).
        </Text>
        <Form form={pwForm} layout="vertical" requiredMark={false} onFinish={submitTerminal}>
          <Form.Item name="password" label="Terminal password" rules={[{ required: true, message: "Enter the terminal password" }]}>
            <Input.Password autoFocus placeholder="separate terminal access password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Terminal — ${deviceId}`}
        open={!!term}
        onCancel={endTerminal}
        footer={[<Button key="c" danger onClick={endTerminal}>Close session</Button>]}
        destroyOnClose
      >
        {term && !term.ready ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Spin />
            <Text style={{ display: "block", marginTop: 14 }}>
              Connecting to the device…
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>
              It brings the encrypted tunnel up on its next check-in (up to ~60s). The
              <b> Open Terminal</b> button appears here the moment it's ready.
            </Text>
          </div>
        ) : (
          <>
            <Text style={{ display: "block", marginBottom: 12 }}>
              ✅ Ready — open your root shell in a new tab.
            </Text>
            <Button type="primary" icon={<CodeOutlined />} onClick={() => term && window.open(term.url, "_blank", "noopener")}>
              Open Terminal
            </Button>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 10 }}>
              The session auto-closes after its TTL; <b>Close session</b> tears it down now.
            </Text>
          </>
        )}
      </Modal>

      <Table
        rowKey="_id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={commands}
        pagination={false}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No commands yet" /> }}
      />
    </Card>
  );
}
